<script lang="ts" setup>
const { t: $t } = useI18n()

import { joinURL } from 'ufo'
import type { DropdownOption } from 'naive-ui'
import type { MachineryCategories } from '~/types'
import type { CustomerModuleDefectCreateOrUpdatePayload } from '~/layers/customer-module/types'

const props = defineProps<{ machineryId: string }>()

const { machinery: queryMachinery } = useQuery()
const { data: machinery, isLoading, error, suspense } = queryMachinery.public.byId(computed(() => props.machineryId))
onServerPrefetch(suspense)

const isForeignMachinery = computed(() => !!machinery.value?.isForeignMachinery)
const isApproved = computed(() => machinery.value?.status === 'approval')
const machineryCategory = computed(() => machinery.value ? machinery.value.category as MachineryCategories : undefined)
const isMachineryReleasedForOnlineSale = computed(() => machinery.value?.isReleasedForOnlineSale)
// Only conditions for this specific user are fetched, so we just need to check if there are any.
const isMachineryReleasedForThisPartnerSaleUser = computed(() => (machinery.value?.partnerConditionsForPartnersThatCanSellMe ?? []).length > 0)

const isSalesView = useRouteQueryAsBoolean('salesView', false)

const { status } = useAuth()
const { hasAnyAssignedRole } = useAuthorization()

const { machineryHeadingsByMachineCategory: showMachineryHeadingsByMachineCategory } = useFormOptionalConditions()

const { foreignMachineryQuery, accountQuery, machineryQuery } = useCustomerModuleQuery()
const { data: canManageDefects } = foreignMachineryQuery.canManageDefects(computed(() => props.machineryId), computed(() => status.value === 'authenticated'))
const machineryIdForDefectPopup = ref<string | undefined>(undefined)
const defectCreatePayload = ref<CustomerModuleDefectCreateOrUpdatePayload | undefined>(undefined)
const publicDefectCreatePayload = ref<{ machineryId: string, isForeignMachineryDefect: boolean } | undefined>(undefined)

const pageDescription = computed(() => {
  if (!machinery.value) {
    return undefined
  }
  return `${machinery.value.producerCompanyName} ${$t(`machinery.rubric.${machinery.value.machineryRubric}`)} ${machinery.value.type.name}`
})

function openPDF(id: string) {
  window.open(joinURL('/api/pdf/machinery/details', id), '_blank')
}

const hasGalleryMedia = computed(() => (
  (machinery.value?.marketingPhotos && machinery.value.marketingPhotos.length > 0)
  || (machinery.value?.marketingVideos && machinery.value.marketingVideos.length > 0)
  || (machinery.value?.externalMarketingVideos && machinery.value.externalMarketingVideos.length > 0)
))

const isPartnerSales = hasAnyAssignedRole(['partnerSales'], false)
const isCustomerUser = hasAnyAssignedRole(['customerUser'], false)
const canManageRental = computed(() => isCustomerUser.value || status.value === 'unauthenticated')
const { data: account } = accountQuery.getSelf(isCustomerUser)
const { data: canPartnerSellMachinery } = machineryQuery.partnerSales.canSellMachinery(computed(() => props.machineryId), isPartnerSales)
const { data: machineryPartner, isLoading: isLoadingPartner } = machineryQuery.partnerSales.getDetailsById(computed(() => props.machineryId), isPartnerSales)

const customerType = computed(() => account.value?.contactPerson?.customer?.type)

const isReservationPopupOpen = ref(false)

const { copy: copyToClipboard, isSupported: isCopyToClipboardSupported } = useClipboard()
const notification = useNotification()
function copyMachineryData() {
  const copyText = formatMachineryDetailsForCopy(machinery.value)
  if (!copyText) {
    notification.warning({
      title: $t('customerModule.machinery.details.copyMachineryData.warningTitle'),
      content: $t('customerModule.machinery.details.copyMachineryData.warningContent'),
      duration: 6500,
    })
    return
  }

  copyToClipboard(copyText)
  notification.success({
    title: $t('customerModule.machinery.details.copyMachineryData.successTitle'),
    content: `${$t('customerModule.machinery.details.copyMachineryData.successContent')}:\n\n${copyText}`,
    duration: 4500,
  })
}

function openDefectCreatePopup() {
  if (status.value === 'authenticated') {
    return defectCreatePayload.value = {
      mode: 'create',
      entity: 'machinery',
      entityId: props.machineryId,
      isForeignMachineryDefect: !!isForeignMachinery.value,
      isAllowedForPublic: true,
    }
  }
  return publicDefectCreatePayload.value = {
    machineryId: props.machineryId,
    isForeignMachineryDefect: !!isForeignMachinery.value,
  }
}

const headerActionOptions = computed((): DropdownOption[] => [
  {
    label: $t('customerModule.machinery.details.machinery.manageRental'),
    key: 'manageRental',
    icon: () => useRenderIcon({ name: 'material-symbols:folder-managed-outline' }),
    show: !isSalesView.value && !isForeignMachinery.value && canManageRental.value,
  },
  {
    label: $t('customerModule.machinery.details.copyMachineryData.label'),
    key: 'copyMachineryData',
    icon: () => useRenderIcon({ name: 'material-symbols:folder-managed-outline' }),
    show: isCopyToClipboardSupported.value && isPartnerSales.value,
  },
  {
    label: $t('customerModule.machinery.details.reserveForSale'),
    key: 'reserveForSale',
    show: !!canPartnerSellMachinery.value,
  },
  {
    label: $t('customerModule.machinery.details.reportDefect'),
    key: 'reportDefect',
    icon: () => useRenderIcon({ name: 'material-symbols:assignment-late-outline' }),
    show: isApproved.value,
  },
  {
    label: $t('customerModule.machinery.details.machinery.printPage'),
    key: 'printPage',
    icon: () => useRenderIcon({ name: 'material-symbols:print-rounded' }),
    show: status.value === 'authenticated',
  },
])

function headerActionOnClick(key: string) {
  if (key === 'manageRental') {
    return navigateTo('/my-hos/rental/manage')
  }
  if (key === 'copyMachineryData') {
    return copyMachineryData()
  }
  if (key === 'reserveForSale') {
    return isReservationPopupOpen.value = true
  }
  if (key === 'reportDefect') {
    return openDefectCreatePopup()
  }
  if (key === 'printPage') {
    return machinery.value && openPDF(machinery.value.id)
  }
}

const isMobile = useIsMobileScreen()
</script>

<template>
  <CustomerModulePageWrapper
    :title="machineryId.toUpperCase()"
    :description="pageDescription"
    :breadcrumbs="false"
    :padding="false"
    :rounded="false"
    header-size="medium"
  >
    <template #headerExtra>
      <n-dropdown
        trigger="click"
        :placement="isMobile ? 'bottom' : 'bottom-end'"
        :options="headerActionOptions"
        :width="isMobile ? 'trigger' : undefined"
        @select="headerActionOnClick"
      >
        <n-button icon-placement="right" type="primary" size="large">
          <template #icon>
            <Icon name="material-symbols:menu" />
          </template>
          {{ $t('customerModule.machinery.details.header.actions.label') }}
        </n-button>
      </n-dropdown>
    </template>

    <div class="absolute bg-hos-blue-dark w-full h-[5vh]" />

    <CustomerModuleLayoutStickyScrollHeader :show-height="85">
      <p class="text-lg md:text-2xl text-white">
        <span class="font-bold">
          {{ machineryId.toUpperCase() }}
        </span>
        - {{ pageDescription }}
      </p>
    </CustomerModuleLayoutStickyScrollHeader>

    <div class="relative PagePadding mb-8">
      <CustomerModuleEmptyPlaceholder
        v-if="(!isLoading && !machinery) || error"
        :title="$t('customerModule.machinery.details.machinery.notFound')"
        icon-name="material-symbols:forklift"
      />

      <template v-else>
        <div class="grid items-start lg:grid-cols-10 xl:grid-cols-7 gap-4">
          <!-- Left sticky column -->
          <ClientOnly>
            <div class="lg:col-span-7 xl:col-span-5 lg:sticky lg:top-[8.5rem]">
              <div class="mb-4">
                <CustomerModuleCarousel
                  :files="machinery?.marketingPhotos ?? []"
                  :with-preview="true"
                  image-class="MachineryDetailsImageHeight w-full bg-hos-blue"
                />
              </div>

              <CustomerModuleMachineryDetailsPartnerSalesReservations
                v-if="isPartnerSales && (isMachineryReleasedForOnlineSale || isMachineryReleasedForThisPartnerSaleUser)"
                :machinery-id
              />
            </div>

            <template #fallback>
              <n-skeleton
                class="MachineryDetailsImageHeight lg:col-span-7 xl:col-span-5 h-[300px] md:h-[600px] w-full"
                :sharp="false"
              />
            </template>
          </ClientOnly>

          <!-- Right details column -->
          <div class="lg:col-span-3 xl:col-span-2 space-y-4">
            <CustomerModuleMachineryDetailsPartnerSalesReleaseCondition
              v-if="isPartnerSales && (isMachineryReleasedForOnlineSale || isMachineryReleasedForThisPartnerSaleUser)"
              :machinery="machineryPartner"
              :is-loading="isLoadingPartner"
            />
            <CustomerModuleMachineryDetailsContact v-if="!isForeignMachinery" :customer-type :machinery :is-loading :is-sales-view="isSalesView" />
            <CustomerModuleMachineryDetailsVehicleData :machinery :is-loading />
            <CustomerModuleMachineryDetailsType :machinery :is-loading />
            <CustomerModuleMachineryDetailsMeasurements :machinery :is-loading />
            <CustomerModuleMachineryDetailsPlatform
              v-if="machineryCategory && useShowFieldByFilters(showMachineryHeadingsByMachineCategory, 'platform', machineryCategory)"
              :machinery
              :is-loading
            />
            <CustomerModuleMachineryDetailsMast
              v-if="machineryCategory && useShowFieldByFilters(showMachineryHeadingsByMachineCategory, 'mastdaten', machineryCategory)"
              :machinery
              :is-loading
            />
            <CustomerModuleMachineryDetailsTires
              :machinery
              :is-loading
            />
            <CustomerModuleMachineryDetailsMotor
              v-if="machineryCategory && useShowFieldByFilters(showMachineryHeadingsByMachineCategory, 'antrieb', machineryCategory)"
              :machinery
              :is-loading
            />
            <CustomerModuleMachineryDetailsBattery
              v-if="machineryCategory && useShowFieldByFilters(showMachineryHeadingsByMachineCategory, 'batteriedaten', machineryCategory)"
              :machinery
              :is-loading
            />
            <CustomerModuleMachineryDetailsEquipment :machinery :is-loading />
          </div>
        </div>

        <!-- Bottom panel -->

        <!-- QuickSale section is duplicated when User having both customerUser and PartnerSale
        user role view this page. Added !isPartnerSales check here to ensure no duplication. -->
        <div v-if="isCustomerUser && !isPartnerSales && isMachineryReleasedForOnlineSale" class="mt-4">
          <CustomerModuleMachineryDetailsCustomerUserQuickSaleSection :machinery-id />
        </div>

        <div v-if="isPartnerSales && (isMachineryReleasedForOnlineSale || isMachineryReleasedForThisPartnerSaleUser)" class="mt-4">
          <CustomerModuleMachineryDetailsPartnerSalesPanelWrapper :machinery-id />
        </div>

        <CustomerModuleMachineryDetailsGallery v-if="hasGalleryMedia" :machinery class="mt-4" />
      </template>
    </div>

    <CustomerModuleDefectPopup
      v-if="canManageDefects && machineryIdForDefectPopup"
      entity="machinery"
      :entity-id="machineryIdForDefectPopup"
      @close="machineryIdForDefectPopup = undefined"
    />

    <CustomerModuleDefectCreateOrUpdateContainer
      :payload="defectCreatePayload"
      @close="defectCreatePayload = undefined"
    />

    <CustomerModuleDefectPublicCreatePopup
      v-if="publicDefectCreatePayload"
      :payload="publicDefectCreatePayload"
      @close="publicDefectCreatePayload = undefined"
    />

    <CustomerModuleSaleReservationPopup
      v-if="isReservationPopupOpen"
      :data="{ machineryId }"
      @close="isReservationPopupOpen = false"
    />
  </CustomerModulePageWrapper>
</template>

<style>
 @media only screen {
  .MachineryDetailsImageHeight {
    @apply h-[300px] md:h-[600px]
  }
}
</style>
